/**
 * MarkdownRenderer -- zero-dependency pure-React markdown renderer.
 *
 * No external packages needed. Handles the elements the AI response produces:
 *   Block: headings (h1-h4), paragraphs, bullet lists, numbered lists,
 *          blockquotes, code blocks, horizontal rules.
 *   Inline: **bold**, *italic*, `code`, currency amounts (Rs.), percentages.
 *
 * Styled with Tailwind classes using FinminOS design tokens.
 * Works in compact mode (sidebar) and full mode (workspace summary).
 */
import * as React from 'react';
import { cn } from '../lib/cn';

export interface MarkdownRendererProps {
  children: string;
  className?: string;
  compact?: boolean;
}

// ---------------------------------------------------------------------------
// Inline parser
// ---------------------------------------------------------------------------

// Splits text on **bold**, *italic*, `code`, Rs.amount, N%
// NOTE: regex has `g` flag -- always reset lastIndex before calling exec()
const INLINE_RE =
  /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|₹\s*[\d,]+(?:\.\d+)?|\b\d[\d,]*(?:\.\d+)?%)/g;

function renderInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  INLINE_RE.lastIndex = 0;
  let last = 0;
  let kid = 0;
  let m: RegExpExecArray | null;

  while ((m = INLINE_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const raw = m[0];
    const k = kid++;

    if (raw.startsWith('**')) {
      nodes.push(
        <strong key={k} className="font-semibold text-foreground">
          {raw.slice(2, -2)}
        </strong>,
      );
    } else if (raw.startsWith('*')) {
      nodes.push(
        <em key={k} className="italic text-muted-foreground">
          {raw.slice(1, -1)}
        </em>,
      );
    } else if (raw.startsWith('`')) {
      nodes.push(
        <code
          key={k}
          className="rounded px-1.5 py-0.5 font-mono text-[0.85em] font-medium bg-mint text-forest"
        >
          {raw.slice(1, -1)}
        </code>,
      );
    } else {
      // currency or percentage -- highlight with a subtle forest tint
      nodes.push(
        <mark
          key={k}
          className="rounded px-1 not-italic font-semibold text-forest"
          style={{ backgroundColor: 'hsl(var(--palette-forest) / 0.08)' }}
        >
          {raw}
        </mark>,
      );
    }
    last = m.index + raw.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

// ---------------------------------------------------------------------------
// Heading helper (avoids dynamic JSX tag TypeScript issues)
// ---------------------------------------------------------------------------

function Heading({
  level,
  children,
  className,
}: {
  level: number;
  children: React.ReactNode;
  className: string;
}) {
  if (level === 1) return <h1 className={className}>{children}</h1>;
  if (level === 2) return <h2 className={className}>{children}</h2>;
  if (level === 3) return <h3 className={className}>{children}</h3>;
  return <h4 className={className}>{children}</h4>;
}

// ---------------------------------------------------------------------------
// Block parser
// ---------------------------------------------------------------------------

// Checks whether a line starts a new block element (so paragraph accumulation
// stops at the right place).
function isBlockStart(line: string): boolean {
  const t = line.trim();
  return (
    t === '' ||
    /^#{1,4}\s/.test(t) ||
    /^[-*+]\s/.test(t) ||
    /^\d+\.\s/.test(t) ||
    t.startsWith('```') ||
    t.startsWith('> ') ||
    /^(-{3,}|\*{3,}|_{3,})$/.test(t)
  );
}

function parseBlocks(md: string, compact: boolean): React.ReactNode[] {
  const lines = md.split('\n');
  const out: React.ReactNode[] = [];
  let i = 0;
  let bk = 0; // block key

  while (i < lines.length) {
    const line = lines[i]!;
    const t = line.trim();

    // Empty line
    if (t === '') { i++; continue; }

    // Fenced code block
    if (t.startsWith('```')) {
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.trim().startsWith('```')) {
        codeLines.push(lines[i]!);
        i++;
      }
      i++; // closing ```
      out.push(
        <pre
          key={bk++}
          className={cn(
            'rounded-lg overflow-x-auto font-mono bg-forest text-cream',
            compact ? 'text-[10px] p-2 my-1' : 'text-sm p-3 my-2',
          )}
        >
          <code>{codeLines.join('\n')}</code>
        </pre>,
      );
      continue;
    }

    // Heading
    const hm = t.match(/^(#{1,4})\s+(.+)/);
    if (hm) {
      const level = hm[1]!.length;
      const sizeClass =
        level === 1
          ? compact ? 'text-sm font-bold'      : 'text-xl font-bold'
          : level === 2
          ? compact ? 'text-xs font-semibold'  : 'text-base font-semibold'
          : compact   ? 'text-xs font-medium'  : 'text-sm font-semibold';
      out.push(
        <Heading
          key={bk++}
          level={level}
          className={cn(
            'text-foreground leading-snug',
            sizeClass,
            compact ? 'mt-2 mb-0.5' : 'mt-3 mb-1',
          )}
        >
          {renderInline(hm[2]!)}
        </Heading>,
      );
      i++;
      continue;
    }

    // Blockquote
    if (t.startsWith('> ')) {
      const qLines: string[] = [];
      while (i < lines.length && lines[i]!.trim().startsWith('> ')) {
        qLines.push(lines[i]!.trim().slice(2));
        i++;
      }
      out.push(
        <blockquote
          key={bk++}
          className={cn(
            'border-l-2 border-forest pl-3 italic text-muted-foreground',
            compact ? 'text-[10px] my-1' : 'text-sm my-2',
          )}
        >
          {qLines.map((ql, qi) => (
            <p key={qi}>{renderInline(ql)}</p>
          ))}
        </blockquote>,
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(t)) {
      out.push(<hr key={bk++} className="border-border my-3" />);
      i++;
      continue;
    }

    // Bullet list
    if (/^[-*+]\s/.test(t)) {
      const items: React.ReactNode[] = [];
      while (
        i < lines.length &&
        lines[i]!.trim() !== '' &&
        /^\s*[-*+]\s/.test(lines[i]!)
      ) {
        const itemText = lines[i]!.trim().replace(/^[-*+]\s/, '');
        items.push(
          <li
            key={i}
            className={cn(
              'text-muted-foreground leading-relaxed',
              compact ? 'text-[10px]' : 'text-sm',
            )}
          >
            {renderInline(itemText)}
          </li>,
        );
        i++;
      }
      out.push(
        <ul
          key={bk++}
          className={cn(
            'list-disc list-outside pl-4 marker:text-forest',
            compact ? 'space-y-0 my-1' : 'space-y-0.5 my-1.5',
          )}
        >
          {items}
        </ul>,
      );
      continue;
    }

    // Numbered list
    if (/^\d+\.\s/.test(t)) {
      const items: React.ReactNode[] = [];
      while (
        i < lines.length &&
        lines[i]!.trim() !== '' &&
        /^\s*\d+\.\s/.test(lines[i]!)
      ) {
        const itemText = lines[i]!.trim().replace(/^\d+\.\s/, '');
        items.push(
          <li
            key={i}
            className={cn(
              'text-muted-foreground leading-relaxed',
              compact ? 'text-[10px]' : 'text-sm',
            )}
          >
            {renderInline(itemText)}
          </li>,
        );
        i++;
      }
      out.push(
        <ol
          key={bk++}
          className={cn(
            'list-decimal list-outside pl-4 marker:text-forest',
            compact ? 'space-y-0 my-1' : 'space-y-0.5 my-1.5',
          )}
        >
          {items}
        </ol>,
      );
      continue;
    }

    // Paragraph -- accumulate consecutive non-block lines
    const paraLines: string[] = [];
    while (i < lines.length && !isBlockStart(lines[i]!)) {
      paraLines.push(lines[i]!.trim());
      i++;
    }
    if (paraLines.length > 0) {
      out.push(
        <p
          key={bk++}
          className={cn(
            'text-muted-foreground leading-relaxed',
            compact ? 'text-[10px] my-0.5' : 'text-sm my-1',
          )}
        >
          {renderInline(paraLines.join(' '))}
        </p>,
      );
    }
  }

  return out;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function MarkdownRenderer({
  children,
  className,
  compact = false,
}: MarkdownRendererProps) {
  if (!children) return null;
  return (
    <div className={cn('leading-relaxed', className)}>
      {parseBlocks(children, compact)}
    </div>
  );
}
